import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, FileDropZone } from '@beyond-silence/ui';
import type { AccentBlockColor, AccentBlockPosition, AccentBlockVariant } from '@beyond-silence/ui';
import { supabase } from '@/utils/supabase';
import {
  useArticle,
  saveArticle,
  saveTranslation,
} from '../hooks/useSupabaseData';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

type LanguageCode = 'fr' | 'en' | 'ar';

const STEPS = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'] as const;
const LANGUAGES: { code: LanguageCode; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية' },
];

// ── Form state shape (maps to articles + article_translations tables) ─
interface EditorForm {
  // Step 1: Headline & Slug
  slug: string;
  category: string;
  translations: Record<LanguageCode, {
    id?: string;
    headline: string;
    meta_description: string;
  }>;
  // Step 2: Body Content
  bodies: Record<LanguageCode, string>;
  pullQuotes: { text: string; side: 'left' | 'right'; language: LanguageCode }[];
  // Step 3: Media
  cover_image_url: string;
  video_url: string;
  imageOrientation: 'landscape' | 'portrait';
  bodyImages: { url: string; alt: string; format: 'landscape' | 'portrait'; type: 'image' | 'video' }[];
  // Step 4: Badges & Metadata
  badgeIds: string[];
  coAuthorIds: string[];
  // Editorial accent block
  accentBlock: {
    enabled: boolean;
    color: AccentBlockColor;
    position: AccentBlockPosition;
    variant: AccentBlockVariant;
  };
  // Article layout template
  layout: string;
}

const EMPTY_FORM: EditorForm = {
  slug: '',
  category: 'features',
  translations: {
    en: { headline: '', meta_description: '' },
    fr: { headline: '', meta_description: '' },
    ar: { headline: '', meta_description: '' },
  },
  bodies: { en: '', fr: '', ar: '' },
  pullQuotes: [],
  cover_image_url: '',
  video_url: '',
  imageOrientation: 'landscape',
  bodyImages: [],
  badgeIds: [],
  coAuthorIds: [],
  accentBlock: {
    enabled: false,
    color: 'yellow',
    position: 'hero-bottom-right',
    variant: 'square',
  },
  layout: 'standard',
};

const CATEGORIES = ['investigation', 'culture', 'opinion', 'features', 'video'];

/**
 * 6-step article editor wizard backed by Supabase articles + article_translations tables.
 * Steps map to the publishing wizard defined in AGENTS.md §4:
 *   1. Headline & Slug    2. Body Content    3. Media
 *   4. Badges & Metadata  5. Access & Donation  6. Review & Publish
 */
export function ArticleEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('staff');
  const navigate = useNavigate();
  const isNew = !id;

  const { article, translations: existingTranslations, loading, error: loadError } = useArticle(id);

  const [form, setForm] = useState<EditorForm>(EMPTY_FORM);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeLang, setActiveLang] = useState<LanguageCode>('en');
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const coverObjectUrl = useRef<string | null>(null);

  // Available badges and authors for pickers
  const [allBadges, setAllBadges] = useState<{ id: string; label: string; color_token: string }[]>([]);
  const [allAuthors, setAllAuthors] = useState<{ id: string; display_name: string }[]>([]);
  const [userRole, setUserRole] = useState<string>('author');

  // Load badges, authors, and current user role
  useEffect(() => {
    supabase.from('badges').select('*').order('label').then(({ data }) => {
      if (data) setAllBadges(data as any);
    });
    supabase.from('profiles').select('id, display_name').in('role', ['author','editor','admin']).order('display_name').then(({ data }) => {
      if (data) setAllAuthors(data as any);
    });
    // Get current user's role for premium layout gating
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        supabase.from('profiles').select('role').eq('id', user.id).single().then(({ data: prof }) => {
          if (prof) setUserRole((prof as any).role ?? 'author');
        });
      }
    });
  }, []);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (coverObjectUrl.current) URL.revokeObjectURL(coverObjectUrl.current);
    };
  }, []);

  // Populate form from existing article data
  useEffect(() => {
    if (!article) return;
    const transMap: EditorForm['translations'] = { ...EMPTY_FORM.translations };
    const bodyMap: EditorForm['bodies'] = { ...EMPTY_FORM.bodies };

    for (const tr of existingTranslations) {
      const lang = tr.language as LanguageCode;
      transMap[lang] = {
        id: tr.id,
        headline: tr.headline ?? '',
        meta_description: tr.meta_description ?? '',
      };
      bodyMap[lang] = tr.body ?? '';
    }

    setForm({
      slug: article.slug ?? '',
      category: article.category ?? 'features',
      translations: transMap,
      bodies: bodyMap,
      cover_image_url: article.cover_image_url ?? '',
      video_url: article.video_url ?? '',
      imageOrientation: (article.cover_image_url?.includes('[portrait]')) ? 'portrait' : 'landscape',
      bodyImages: (article as any).body_images ?? [],
      pullQuotes: (article as any).pull_quotes ?? [],
      badgeIds: [],
      coAuthorIds: [],
      accentBlock: article.accent_block ?? EMPTY_FORM.accentBlock,
      layout: (article as any).layout ?? 'standard',
    });
  }, [article, existingTranslations]);

  // Load existing badge + co-author assignments when editing
  useEffect(() => {
    if (!article?.id) return;
    // Load assigned badges
    supabase.from('article_badges').select('badge_id').eq('article_id', article.id).then(({ data }) => {
      if (data) setForm(f => ({ ...f, badgeIds: (data as any).map((r: any) => r.badge_id) }));
    });
    // Load co-authors
    supabase.from('article_authors').select('author_id').eq('article_id', article.id).neq('role_on_article', 'primary').then(({ data }) => {
      if (data) setForm(f => ({ ...f, coAuthorIds: (data as any).map((r: any) => r.author_id) }));
    });
  }, [article?.id]);

  // ── Save helpers ──────────────────────────────────────────────
  const handleSaveDraft = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const savedArticle = await saveArticle({
        id: article?.id,
        category: form.category,
        slug: form.slug,
        cover_image_url: form.imageOrientation === 'portrait'
          ? `[portrait]${form.cover_image_url.replace('[portrait]', '')}`.replace('[portrait][portrait]', '[portrait]')
          : (form.cover_image_url.replace('[portrait]', '') || undefined),
        video_url: form.video_url || undefined,
        accent_block: form.accentBlock,
        layout: form.layout,
        body_images: form.bodyImages.length > 0 ? form.bodyImages : undefined,
        pull_quotes: form.pullQuotes.length > 0 ? form.pullQuotes : undefined,
      });

      // Save translations that have content
      for (const lang of LANGUAGES) {
        const tr = form.translations[lang.code];
        if (tr.headline.trim() || form.bodies[lang.code].trim()) {
          await saveTranslation({
            article_id: savedArticle.id,
            id: tr.id,
            language: lang.code,
            headline: tr.headline || `[${lang.code.toUpperCase()}] Untitled`,
            body: form.bodies[lang.code] || '',
            meta_description: tr.meta_description || undefined,
          });
        }
      }

      // Save badge assignments
      if (savedArticle.id) {
        // Clear existing badge assignments
        await (supabase.from('article_badges') as any).delete().eq('article_id', savedArticle.id);
        // Insert selected badges
        if (form.badgeIds.length > 0) {
          await (supabase.from('article_badges') as any).insert(
            form.badgeIds.map(badgeId => ({ article_id: savedArticle.id, badge_id: badgeId }))
          );
        }

        // Clear existing co-author assignments (keep primary)
        await (supabase.from('article_authors') as any).delete().eq('article_id', savedArticle.id).neq('role_on_article', 'primary');
        // Insert selected co-authors
        if (form.coAuthorIds.length > 0) {
          await (supabase.from('article_authors') as any).insert(
            form.coAuthorIds.map(authorId => ({ article_id: savedArticle.id, author_id: authorId, role_on_article: 'co-author' }))
          );
        }
      }

      setMessage({ type: 'success', text: 'Draft saved.' });

      // If new, redirect to edit URL so we have an ID
      if (isNew && savedArticle.id) {
        navigate(`/articles/${savedArticle.id}/edit`, { replace: true });
      }
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Save failed' });
    }
    setSaving(false);
  };

  // Submit for review — authors can move their own draft → in_review via RLS.
  const handleSubmitReview = async () => {
    if (!article?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await handleSaveDraft();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: err } = await (supabase.from('articles') as any).update({ status: 'in_review' }).eq('id', article.id);
      if (err) throw err;
      setMessage({ type: 'success', text: 'Submitted for review.' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Submit failed' });
    }
    setSaving(false);
  };

  const handlePublish = async () => {
    if (!article?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await handleSaveDraft();
      // Use publish_article() RPC — only editors/admins can call it.
      // Authors get a clear error; editors/admins get an atomic publish.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: err } = await (supabase.rpc as any)('publish_article', { p_article_id: article.id });
      if (err) throw err;
      setMessage({ type: 'success', text: 'Published!' });
      navigate('/articles');
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Publish failed' });
    }
    setSaving(false);
  };

  // ── Loading / Error states ────────────────────────────────────
  if (!isNew && loading) {
    return (
      <Container size="dashboard" className="flex min-h-[40vh] items-center justify-center py-8">
        <div className="font-ui text-ui-base text-ink-light">{t('states.loading', { ns: 'common' })}</div>
      </Container>
    );
  }

  if (!isNew && loadError) {
    return (
      <Container size="dashboard" className="py-8 text-center">
        <p className="font-ui text-ui-base text-primary">{loadError}</p>
        <button onClick={() => navigate('/articles')} className="mt-4 rounded-pill bg-primary px-4 py-2 font-ui text-ui-sm text-white">
          ← Back to articles
        </button>
      </Container>
    );
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
      {/* Header + actions */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-ui text-ui-lg font-bold text-ink">
          {isNew ? t('articles.newArticle') : t('articles.editArticle')}
        </h1>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="rounded-pill border border-soft bg-white px-3 py-1.5 font-ui text-ui-sm text-ink-light hover:bg-soft/20 disabled:opacity-50 sm:px-4"
          >
            {saving ? t('states.loading', { ns: 'common' }) : t('editor.saveDraft')}
          </button>
          {article?.status === 'draft' && (
            <button
              onClick={handleSubmitReview}
              disabled={saving}
              className="rounded-pill bg-secondary px-3 py-1.5 font-ui text-ui-sm font-medium text-white hover:bg-secondary-700 disabled:opacity-50 sm:px-4"
            >
              {t('editor.submitReview')}
            </button>
          )}
          {(article?.status === 'in_review' || article?.status === 'draft') && currentStep === STEPS.length - 1 && (
            <button
              onClick={handlePublish}
              disabled={saving}
              className="rounded-pill bg-primary px-3 py-1.5 font-ui text-ui-sm font-medium text-white hover:bg-primary-600 disabled:opacity-50 sm:px-4"
            >
              {t('editor.publish')}
            </button>
          )}
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div className={`mt-3 rounded-md px-4 py-2 font-ui text-ui-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-primary/10 text-primary'
        }`}>
          {message.text}
        </div>
      )}

      {/* Step indicator */}
      <div className="mt-4 sm:mt-5 md:mt-6">
        <nav className="hidden sm:flex sm:items-center sm:gap-1" aria-label="Progress">
          {STEPS.map((step, idx) => (
            <button
              key={step}
              onClick={() => setCurrentStep(idx)}
              className={`flex items-center gap-2 rounded-md px-3 py-2 font-ui text-ui-sm font-medium transition-colors
                ${idx === currentStep ? 'bg-primary/10 text-primary'
                  : idx < currentStep ? 'text-ink-light hover:bg-soft/20'
                  : 'text-ink-muted'}
              `.trim().replace(/\s+/g, ' ')}
            >
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-ui-xs font-bold
                ${idx === currentStep ? 'bg-primary text-white'
                  : idx < currentStep ? 'bg-highlight text-ink'
                  : 'bg-soft/60 text-ink-muted'}
              `.trim().replace(/\s+/g, ' ')}>
                {idx < currentStep ? '✓' : idx + 1}
              </span>
              <span className="hidden lg:inline">{t(`editor.${step}`)}</span>
            </button>
          ))}
        </nav>
        <div className="sm:hidden">
          <select
            value={currentStep}
            onChange={(e) => setCurrentStep(Number(e.target.value))}
            className="w-full rounded-md border border-soft/60 bg-white px-3 py-2 font-ui text-ui-sm text-ink"
          >
            {STEPS.map((step, idx) => (
              <option key={step} value={idx}>{idx + 1}. {t(`editor.${step}`)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Step content */}
      <div className="mt-5 rounded-lg border border-soft/60 bg-white p-4 sm:p-5 md:mt-6 md:p-6">
        <h2 className="font-ui text-ui-base font-semibold text-ink">
          {currentStep + 1}. {t(`editor.${STEPS[currentStep]}`)}
        </h2>

        <div className="mt-4 space-y-4">
          {/* ── Step 1: Headline & Slug ─────────────────────────── */}
          {currentStep === 0 && (
            <>
              {/* ── Layout Selector ───────────────────────────── */}
              <div className="rounded-md border border-soft/60 bg-soft/10 p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <span className="font-ui text-ui-sm font-semibold text-ink">Layout:</span>
                  <select
                    value={form.layout}
                    onChange={(e) => setForm(f => ({ ...f, layout: e.target.value }))}
                    className="rounded-md border border-soft/60 bg-white px-3 py-1.5 font-ui text-ui-sm text-ink"
                  >
                    <option value="standard">Standard — Clean reading, images between text</option>
                    <option value="image_hero">Image Hero — Media-led feature</option>
                    <option value="split_media_left">Split: Media Left — Editorial intro</option>
                    <option value="split_media_right">Split: Media Right — Editorial intro</option>
                    <option value="title_first">Title First — Text-led investigation</option>
                    <option value="feature_grid">Feature Grid — Flagship layout</option>
                    {(userRole === 'editor' || userRole === 'admin') && (
                      <option value="editorial_feature_grid">Editorial Feature Grid — Premium magazine layout</option>
                    )}
                    {/* Show current premium layout even for authors (view-only, can't re-select after changing) */}
                    {form.layout === 'editorial_feature_grid' && userRole !== 'editor' && userRole !== 'admin' && (
                      <option value="editorial_feature_grid" disabled>Editorial Feature Grid — Premium (set by editor)</option>
                    )}
                  </select>
                </div>
                <p className="mt-1.5 font-ui text-ui-xs text-ink-light">
                  {form.layout === 'standard' && 'Header → text → images placed between paragraphs. Body images appear where you insert them; landscape images span full width, portrait images float beside text.'}
                  {form.layout === 'image_hero' && 'Large cover image/video dominates the hero. Headline, byline, and body follow below. Requires cover media.'}
                  {form.layout === 'split_media_left' && 'Media on the left (55%), headline + byline on the right. Body below. Requires cover media.'}
                  {form.layout === 'split_media_right' && 'Headline + byline on the left, media on the right (55%). Body below. Requires cover media.'}
                  {form.layout === 'title_first' && 'Large headline block leads. Secondary media is optional and appears below. Body follows.'}
                  {form.layout === 'feature_grid' && '3-column grid: large primary media + headline area + secondary media slot. Body below. Requires cover media.'}
                  {form.layout === 'editorial_feature_grid' && 'Premium 12-column asymmetric magazine grid. Oversized headline + primary media + 0–2 supporting assets + optional accent block. Body in a single elegant column below. Requires cover media. Editor/Admin only.'}
                </p>
                {(userRole !== 'editor' && userRole !== 'admin') && (
                  <p className="mt-1 font-ui text-ui-xs text-ink-muted italic">
                    The Editorial Feature Grid is a premium layout. Request it in an editorial note for editor approval.
                  </p>
                )}
              </div>

              {/* Language tabs */}
              <div className="flex gap-1 border-b border-soft/40 pb-2">
                {LANGUAGES.map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => setActiveLang(code)}
                    className={`rounded-t-md px-3 py-1.5 font-ui text-ui-sm font-medium transition-colors
                      ${activeLang === code ? 'border-b-2 border-primary text-primary' : 'text-ink-light hover:text-ink'}
                    `.trim().replace(/\s+/g, ' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block font-ui text-ui-sm font-medium text-ink">Headline ({activeLang.toUpperCase()})</label>
                <input
                  type="text"
                  value={form.translations[activeLang].headline}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    translations: { ...f.translations, [activeLang]: { ...f.translations[activeLang], headline: e.target.value } },
                  }))}
                  className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-editorial-body text-body-md text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder={`Article headline in ${activeLang.toUpperCase()}`}
                />
              </div>

              <div>
                <label className="block font-ui text-ui-sm font-medium text-ink">Meta Description ({activeLang.toUpperCase()})</label>
                <textarea
                  value={form.translations[activeLang].meta_description}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    translations: { ...f.translations, [activeLang]: { ...f.translations[activeLang], meta_description: e.target.value } },
                  }))}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-editorial-body text-body-md text-ink-light placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Brief summary for SEO and social previews"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block font-ui text-ui-sm font-medium text-ink">Slug</label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-mono text-ui-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="my-article-slug"
                  />
                </div>
                <div>
                  <label className="block font-ui text-ui-sm font-medium text-ink">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-ui text-ui-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{t(`categories.${cat}`, { ns: 'public' })}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Body Content ────────────────────────────── */}
          {currentStep === 1 && (
            <>
              <div className="flex gap-1 border-b border-soft/40 pb-2">
                {LANGUAGES.map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => setActiveLang(code)}
                    className={`rounded-t-md px-3 py-1.5 font-ui text-ui-sm font-medium transition-colors
                      ${activeLang === code ? 'border-b-2 border-primary text-primary' : 'text-ink-light hover:text-ink'}
                    `.trim().replace(/\s+/g, ' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div>
                <label className="block font-ui text-ui-sm font-medium text-ink mb-1">Body ({activeLang.toUpperCase()})</label>
                <div dir={activeLang === 'ar' ? 'rtl' : 'ltr'} className="quill-wrapper">
                  <ReactQuill
                    key={activeLang}
                    value={form.bodies[activeLang]}
                    onChange={(content) => setForm((f) => ({ ...f, bodies: { ...f.bodies, [activeLang]: content } }))}
                    theme="snow"
                    placeholder={{
                      en: 'Write the article body in English...',
                      fr: 'Écrivez le corps de l\'article en français...',
                      ar: '\u200Fاكتب نص المقال باللغة العربية...\u200F',
                    }[activeLang]}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['blockquote', 'code-block'],
                        ['link', 'image'],
                        [{ align: [] }],
                        ['clean'],
                      ],
                    }}
                    style={{ height: '500px', fontFamily: activeLang === 'ar' ? 'Noto Kufi Arabic, sans-serif' : 'Saira, sans-serif' }}
                  />
                </div>
              </div>

              {/* ── Pull Quotes ─────────────────────────────── */}
              <div className="border-t border-soft/40 pt-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-ui text-ui-sm font-semibold text-ink">Pull Quotes</h3>
                  <span className="font-ui text-ui-xs text-ink-light">{form.pullQuotes.length} quote{form.pullQuotes.length !== 1 ? 's' : ''}</span>
                </div>
                <p className="mt-1 font-ui text-ui-xs text-ink-light">
                  Stylized quotes displayed beside the article body. Float left or right with decorative borders.
                </p>

                {form.pullQuotes.map((pq, i) => (
                  <div key={i} className="mt-3 rounded-md border border-soft/60 bg-bg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-ui text-ui-xs font-medium text-ink">Quote {i + 1}</span>
                      <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, pullQuotes: f.pullQuotes.filter((_, j) => j !== i) }))}
                        className="font-ui text-ui-xs text-primary hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      value={pq.text}
                      onChange={(e) => setForm(f => {
                        const qs = [...f.pullQuotes];
                        qs[i] = { ...qs[i], text: e.target.value };
                        return { ...f, pullQuotes: qs };
                      })}
                      rows={2}
                      className="w-full rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-editorial-body text-body-md text-ink italic placeholder:text-ink-muted"
                      placeholder="A memorable quote from the article..."
                      dir={pq.language === 'ar' ? 'rtl' : 'ltr'}
                    />
                    <div className="mt-2 flex gap-3">
                      <select
                        value={pq.side}
                        onChange={(e) => setForm(f => {
                          const qs = [...f.pullQuotes];
                          qs[i] = { ...qs[i], side: e.target.value as 'left' | 'right' };
                          return { ...f, pullQuotes: qs };
                        })}
                        className="rounded-md border border-soft/60 bg-white px-2 py-1.5 font-ui text-ui-xs text-ink"
                      >
                        <option value="left">Float left</option>
                        <option value="right">Float right</option>
                      </select>
                      <select
                        value={pq.language}
                        onChange={(e) => setForm(f => {
                          const qs = [...f.pullQuotes];
                          qs[i] = { ...qs[i], language: e.target.value as LanguageCode };
                          return { ...f, pullQuotes: qs };
                        })}
                        className="rounded-md border border-soft/60 bg-white px-2 py-1.5 font-ui text-ui-xs text-ink"
                      >
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                        <option value="ar">العربية</option>
                      </select>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, pullQuotes: [...f.pullQuotes, { text: '', side: 'right' as const, language: activeLang }] }))}
                  className="mt-3 w-full rounded-md border-2 border-dashed border-soft/60 py-2 font-ui text-ui-sm text-ink-light hover:border-secondary/40 hover:text-secondary transition-colors"
                >
                  + Add pull quote
                </button>
              </div>
            </>
          )}

          {/* ── Step 3: Media ───────────────────────────────────── */}
          {currentStep === 2 && (
            <>
              {/* Placement preview */}
              <div className="rounded-md border border-soft/60 bg-soft/10 p-3">
                <p className="font-ui text-ui-xs font-semibold text-ink">Image placement preview</p>
                <p className="mt-1 font-ui text-ui-xs text-ink-light">
                  {form.layout === 'standard' && 'Cover image appears after the headline. Body images are placed between text paragraphs — landscape spans full width, portrait floats beside text.'}
                  {form.layout === 'image_hero' && 'Cover image/video fills the hero area full-width. Headline and body follow below.'}
                  {form.layout?.startsWith('split_media') && 'Cover image/video occupies ' + (form.layout === 'split_media_left' ? 'left' : 'right') + ' 55% on desktop. Headline and byline on the opposite side.'}
                  {form.layout === 'title_first' && 'Headline dominates. Cover image/video is secondary and appears below the title block.'}
                  {form.layout === 'feature_grid' && 'Primary cover fills a 2×2 grid area. Up to 2 secondary images appear in the remaining grid slot.'}
                  {form.layout === 'editorial_feature_grid' && 'Primary media fills 6–7 columns of a 12-col premium grid. Headline gets 4–5 columns with oversized typography. 0–2 supporting media items appear below the primary.'}
                </p>
              </div>

              {/* Cover image */}
              <div>
                <label className="block font-ui text-ui-sm font-medium text-ink">
                  Cover Image {['image_hero','split_media_left','split_media_right','feature_grid','editorial_feature_grid'].includes(form.layout) && <span className="text-primary">*</span>}
                </label>
                <FileDropZone
                  accept="image/*"
                  isImage
                  label="Drop cover image here or click to browse"
                  hint="JPG, PNG, WebP, AVIF — max 50 MB"
                  previewUrl={(coverPreview ?? form.cover_image_url.replace('[portrait]', '')) || undefined}
                  onFileSelect={(file) => {
                    if (coverObjectUrl.current) URL.revokeObjectURL(coverObjectUrl.current);
                    const url = URL.createObjectURL(file);
                    coverObjectUrl.current = url;
                    setCoverPreview(url);
                    setForm((f) => ({ ...f, cover_image_url: `[local] ${file.name}` }));
                  }}
                  className="mt-1"
                />
                <div className="mt-3">
                  <label className="block font-ui text-ui-xs font-medium text-ink-light">Or paste an image URL</label>
                  <input
                    type="url"
                    value={form.cover_image_url.replace('[portrait]', '')}
                    onChange={(e) => { setCoverPreview(null); setForm((f) => ({ ...f, cover_image_url: e.target.value })); }}
                    className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-mono text-ui-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="https://r2.example.com/images/cover.jpg"
                  />
                </div>
                {form.cover_image_url && (
                  <div className="mt-3">
                    <label className="block font-ui text-ui-xs font-medium text-ink mb-1">Orientation</label>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="imageOrientation" checked={form.imageOrientation === 'landscape'} onChange={() => setForm((f) => ({ ...f, imageOrientation: 'landscape' }))} className="text-primary focus:ring-primary/30" />
                        <span className="font-ui text-ui-sm text-ink">Landscape</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="imageOrientation" checked={form.imageOrientation === 'portrait'} onChange={() => setForm((f) => ({ ...f, imageOrientation: 'portrait' }))} className="text-primary focus:ring-primary/30" />
                        <span className="font-ui text-ui-sm text-ink">Portrait</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Video URL */}
              <div>
                <label className="block font-ui text-ui-sm font-medium text-ink">Video URL (YouTube / Vimeo)</label>
                <input
                  type="url"
                  value={form.video_url}
                  onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-mono text-ui-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>

              {/* Body images — standard layout */}
              {form.layout === 'standard' && (
                <div className="border-t border-soft/40 pt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-ui text-ui-sm font-semibold text-ink">Body Images</h3>
                    <span className="font-ui text-ui-xs text-ink-light">{form.bodyImages.length}/3</span>
                  </div>
                  <p className="mt-1 font-ui text-ui-xs text-ink-light">
                    Images placed between text paragraphs. Landscape = full width. Portrait = text wraps beside.
                  </p>
                  {form.bodyImages.map((img, i) => (
                    <div key={i} className="mt-3 rounded-md border border-soft/60 bg-bg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-ui text-ui-xs font-medium text-ink">Image {i + 1}</span>
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, bodyImages: f.bodyImages.filter((_, j) => j !== i) }))}
                          className="font-ui text-ui-xs text-primary hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                      <input
                        type="url"
                        value={img.url}
                        onChange={(e) => setForm(f => {
                          const imgs = [...f.bodyImages];
                          imgs[i] = { ...imgs[i], url: e.target.value };
                          return { ...f, bodyImages: imgs };
                        })}
                        className="w-full rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-mono text-ui-xs text-ink"
                        placeholder={(img.type || 'image') === 'video' ? 'https://www.youtube.com/watch?v=...' : 'https://r2.example.com/images/body-1.jpg'}
                      />
                      <div className="mt-2 flex gap-2">
                        <select
                          value={img.type || 'image'}
                          onChange={(e) => setForm(f => {
                            const imgs = [...f.bodyImages];
                            imgs[i] = { ...imgs[i], type: e.target.value as 'image' | 'video' };
                            return { ...f, bodyImages: imgs };
                          })}
                          className="rounded-md border border-soft/60 bg-white px-2 py-1.5 font-ui text-ui-xs text-ink"
                        >
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                        </select>
                        <input
                          type="text"
                          value={img.alt}
                          onChange={(e) => setForm(f => {
                            const imgs = [...f.bodyImages];
                            imgs[i] = { ...imgs[i], alt: e.target.value };
                            return { ...f, bodyImages: imgs };
                          })}
                          className="flex-1 rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-ui text-ui-xs text-ink"
                          placeholder="Alt text / caption"
                        />
                        {(img.type || 'image') === 'image' && (
                          <select
                            value={img.format}
                            onChange={(e) => setForm(f => {
                              const imgs = [...f.bodyImages];
                              imgs[i] = { ...imgs[i], format: e.target.value as 'landscape' | 'portrait' };
                              return { ...f, bodyImages: imgs };
                            })}
                            className="rounded-md border border-soft/60 bg-white px-2 py-1.5 font-ui text-ui-xs text-ink"
                          >
                            <option value="landscape">Landscape</option>
                            <option value="portrait">Portrait</option>
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                  {form.bodyImages.length < 3 && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, bodyImages: [...f.bodyImages, { url: '', alt: '', format: 'landscape', type: 'image' }] }))}
                      className="mt-3 w-full rounded-md border-2 border-dashed border-soft/60 py-2 font-ui text-ui-sm text-ink-light hover:border-secondary/40 hover:text-secondary transition-colors"
                    >
                      + Add body image / video
                    </button>
                  )}
                </div>
              )}

              {/* Secondary images — feature_grid */}
              {form.layout === 'feature_grid' && (
                <div className="border-t border-soft/40 pt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-ui text-ui-sm font-semibold text-ink">Secondary Images</h3>
                    <span className="font-ui text-ui-xs text-ink-light">{form.bodyImages.length}/2</span>
                  </div>
                  <p className="mt-1 font-ui text-ui-xs text-ink-light">
                    Appear in the secondary grid slot. Landscape orientation recommended.
                  </p>
                  {form.bodyImages.map((img, i) => (
                    <div key={i} className="mt-3 rounded-md border border-soft/60 bg-bg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-ui text-ui-xs font-medium text-ink">Secondary {i + 1}</span>
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, bodyImages: f.bodyImages.filter((_, j) => j !== i) }))}
                          className="font-ui text-ui-xs text-primary hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                      <input
                        type="url"
                        value={img.url}
                        onChange={(e) => setForm(f => { const imgs = [...f.bodyImages]; imgs[i] = { ...imgs[i], url: e.target.value }; return { ...f, bodyImages: imgs }; })}
                        className="w-full rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-mono text-ui-xs text-ink"
                        placeholder="https://r2.example.com/images/secondary-1.jpg"
                      />
                      <div className="mt-2">
                        <input
                          type="text"
                          value={img.alt}
                          onChange={(e) => setForm(f => { const imgs = [...f.bodyImages]; imgs[i] = { ...imgs[i], alt: e.target.value }; return { ...f, bodyImages: imgs }; })}
                          className="w-full rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-ui text-ui-xs text-ink"
                          placeholder="Alt text"
                        />
                      </div>
                    </div>
                  ))}
                  {form.bodyImages.length < 2 && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, bodyImages: [...f.bodyImages, { url: '', alt: '', format: 'landscape', type: 'image' }] }))}
                      className="mt-3 w-full rounded-md border-2 border-dashed border-soft/60 py-2 font-ui text-ui-sm text-ink-light hover:border-secondary/40 hover:text-secondary transition-colors"
                    >
                      + Add secondary image
                    </button>
                  )}
                </div>
              )}

              {/* Supporting media — editorial_feature_grid */}
              {form.layout === 'editorial_feature_grid' && (
                <div className="border-t border-soft/40 pt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-ui text-ui-sm font-semibold text-ink">Supporting Media</h3>
                    <span className="font-ui text-ui-xs text-ink-light">{form.bodyImages.length}/2</span>
                  </div>
                  <p className="mt-1 font-ui text-ui-xs text-ink-light">
                    Optional supporting images/videos for the premium magazine grid. Appear below the primary media in the hero. Landscape orientation recommended.
                  </p>
                  {form.bodyImages.map((img, i) => (
                    <div key={i} className="mt-3 rounded-md border border-soft/60 bg-bg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-ui text-ui-xs font-medium text-ink">Supporting {i + 1}</span>
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, bodyImages: f.bodyImages.filter((_, j) => j !== i) }))}
                          className="font-ui text-ui-xs text-primary hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                      <input
                        type="url"
                        value={img.url}
                        onChange={(e) => setForm(f => { const imgs = [...f.bodyImages]; imgs[i] = { ...imgs[i], url: e.target.value }; return { ...f, bodyImages: imgs }; })}
                        className="w-full rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-mono text-ui-xs text-ink"
                        placeholder="https://r2.example.com/images/supporting-1.jpg"
                      />
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={img.alt}
                          onChange={(e) => setForm(f => { const imgs = [...f.bodyImages]; imgs[i] = { ...imgs[i], alt: e.target.value }; return { ...f, bodyImages: imgs }; })}
                          className="flex-1 rounded-md border border-soft/60 bg-bg px-3 py-1.5 font-ui text-ui-xs text-ink"
                          placeholder="Alt text / caption"
                        />
                        <select
                          value={img.type || 'image'}
                          onChange={(e) => setForm(f => { const imgs = [...f.bodyImages]; imgs[i] = { ...imgs[i], type: e.target.value as 'image' | 'video' }; return { ...f, bodyImages: imgs }; })}
                          className="rounded-md border border-soft/60 bg-white px-2 py-1.5 font-ui text-ui-xs text-ink"
                        >
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                        </select>
                      </div>
                    </div>
                  ))}
                  {form.bodyImages.length < 2 && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, bodyImages: [...f.bodyImages, { url: '', alt: '', format: 'landscape', type: 'image' }] }))}
                      className="mt-3 w-full rounded-md border-2 border-dashed border-soft/60 py-2 font-ui text-ui-sm text-ink-light hover:border-secondary/40 hover:text-secondary transition-colors"
                    >
                      + Add supporting media
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Step 4: Badges & Metadata ───────────────────────── */}
          {currentStep === 3 && (
            <>
              {/* ── Badge Picker ───────────────────────────────── */}
              <div>
                <h3 className="font-ui text-ui-sm font-semibold text-ink">Badges</h3>
                <p className="mt-1 font-ui text-ui-xs text-ink-light">Select up to 3 badges for this article.</p>
                {allBadges.length === 0 ? (
                  <p className="mt-2 font-ui text-ui-xs text-ink-light">Loading badges…</p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {allBadges.map((badge) => {
                      const selected = form.badgeIds.includes(badge.id);
                      const atLimit = form.badgeIds.length >= 3 && !selected;
                      return (
                        <button
                          key={badge.id}
                          type="button"
                          disabled={atLimit}
                          onClick={() => setForm(f => ({
                            ...f,
                            badgeIds: selected
                              ? f.badgeIds.filter(id => id !== badge.id)
                              : [...f.badgeIds, badge.id],
                          }))}
                          className={`rounded-pill px-3 py-1 font-ui text-ui-xs font-medium transition-colors border
                            ${selected
                              ? 'border-ink/20 text-white'
                              : 'border-soft/60 text-ink-light hover:border-ink/30'}
                            ${atLimit ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                          `.trim().replace(/\s+/g, ' ')}
                          style={selected ? { backgroundColor: badge.color_token } : undefined}
                        >
                          {badge.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Co-author Picker ────────────────────────────── */}
              <div className="mt-5 border-t border-soft/40 pt-4">
                <h3 className="font-ui text-ui-sm font-semibold text-ink">Co-authors</h3>
                <p className="mt-1 font-ui text-ui-xs text-ink-light">
                  Add contributing authors. The article owner is the primary author by default.
                </p>
                {allAuthors.length === 0 ? (
                  <p className="mt-2 font-ui text-ui-xs text-ink-light">Loading authors…</p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {allAuthors
                      .filter(a => a.id !== (article as any)?.author_id)
                      .map((author) => {
                        const selected = form.coAuthorIds.includes(author.id);
                        return (
                          <button
                            key={author.id}
                            type="button"
                            onClick={() => setForm(f => ({
                              ...f,
                              coAuthorIds: selected
                                ? f.coAuthorIds.filter(id => id !== author.id)
                                : [...f.coAuthorIds, author.id],
                            }))}
                            className={`rounded-pill px-3 py-1 font-ui text-ui-xs font-medium transition-colors border cursor-pointer
                              ${selected
                                ? 'border-secondary bg-secondary/10 text-secondary'
                                : 'border-soft/60 text-ink-light hover:border-secondary/30'}
                            `.trim().replace(/\s+/g, ' ')}
                          >
                            {author.display_name}
                          </button>
                        );
                      })}
                  </div>
                )}
                {form.coAuthorIds.length > 0 && (
                  <p className="mt-2 font-ui text-ui-xs text-ink-light">
                    {form.coAuthorIds.length} co-author{form.coAuthorIds.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* ── Editorial Accent Block ──────────────────────── */}
              <div className="mt-5 border-t border-soft/40 pt-4">
                <h3 className="font-ui text-ui-sm font-semibold text-ink">
                  Editorial Accent Block
                </h3>
                <p className="mt-1 font-ui text-ui-xs text-ink-light">
                  Add a decorative geometric color panel behind/adjacent to the article hero or content.
                </p>

                {/* Enable toggle */}
                <label className="mt-3 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.accentBlock.enabled}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      accentBlock: { ...f.accentBlock, enabled: e.target.checked },
                    }))}
                    className="h-4 w-4 rounded border-soft/60 text-primary focus:ring-primary/30"
                  />
                  <span className="font-ui text-ui-sm text-ink">Enable accent block</span>
                </label>

                {form.accentBlock.enabled && (
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {/* Color */}
                    <div>
                      <label className="block font-ui text-ui-xs font-medium text-ink">Color</label>
                      <select
                        value={form.accentBlock.color}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          accentBlock: { ...f.accentBlock, color: e.target.value as AccentBlockColor },
                        }))}
                        className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-ui text-ui-sm text-ink"
                      >
                        <option value="yellow">Yellow</option>
                        <option value="blue">Blue</option>
                        <option value="softBlue">Soft Blue</option>
                        <option value="red">Red</option>
                      </select>
                    </div>

                    {/* Position */}
                    <div>
                      <label className="block font-ui text-ui-xs font-medium text-ink">Placement</label>
                      <select
                        value={form.accentBlock.position}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          accentBlock: { ...f.accentBlock, position: e.target.value as AccentBlockPosition },
                        }))}
                        className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-ui text-ui-sm text-ink"
                      >
                        <option value="hero-top-left">Hero top-left</option>
                        <option value="hero-top-right">Hero top-right</option>
                        <option value="hero-bottom-left">Hero bottom-left</option>
                        <option value="hero-bottom-right">Hero bottom-right</option>
                        <option value="media-left">Media left</option>
                        <option value="media-right">Media right</option>
                        <option value="section-left">Section left</option>
                        <option value="section-right">Section right</option>
                      </select>
                    </div>

                    {/* Variant */}
                    <div>
                      <label className="block font-ui text-ui-xs font-medium text-ink">Shape</label>
                      <select
                        value={form.accentBlock.variant}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          accentBlock: { ...f.accentBlock, variant: e.target.value as AccentBlockVariant },
                        }))}
                        className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-ui text-ui-sm text-ink"
                      >
                        <option value="square">Square</option>
                        <option value="portrait">Portrait</option>
                        <option value="landscape">Landscape</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Mini preview — shows the color chip at selected variant ratio */}
                {form.accentBlock.enabled && (
                  <div className="mt-3 rounded-md border border-soft/60 bg-bg p-4">
                    <p className="font-ui text-ui-xs text-ink-light mb-2">Preview:</p>
                    <div
                      className={`
                        rounded-sm opacity-20
                        ${form.accentBlock.variant === 'landscape' ? 'w-24 h-18' :
                          form.accentBlock.variant === 'portrait' ? 'w-18 h-24' : 'w-16 h-16'}
                      `.trim().replace(/\s+/g, ' ')}
                      style={{
                        backgroundColor:
                          form.accentBlock.color === 'yellow' ? '#ebc326' :
                          form.accentBlock.color === 'blue' ? '#2e7eb3' :
                          form.accentBlock.color === 'softBlue' ? '#bfdae1' : '#B2180A',
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Step 5: Access & Donation Config ─────────────────── */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="rounded-md border border-soft/60 bg-bg p-4">
                <h3 className="font-ui text-ui-sm font-semibold text-ink">
                  {t('editor.step5AccessLabel')}
                </h3>
                <p className="mt-1 font-ui text-ui-sm text-ink">
                  <strong>{article?.access_level ?? 'free'}</strong>
                </p>
                <p className="mt-2 font-ui text-ui-xs text-ink-light">
                  {t('editor.step5AccessDesc')}
                </p>
              </div>
              <div className="rounded-md border border-soft/60 bg-bg p-4">
                <h3 className="font-ui text-ui-sm font-semibold text-ink">
                  {t('editor.step5DonationLabel')}
                </h3>
                <p className="mt-2 font-ui text-ui-xs text-ink-light">
                  {t('editor.step5DonationDesc')}
                </p>
              </div>
            </div>
          )}

          {/* ── Step 6: Review & Publish ─────────────────────────── */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <p className="font-ui text-ui-sm text-ink-light">
                Review your article before publishing. Publishing is an atomic RPC action and requires editor or admin role.
              </p>

              {/* Preview: show what's been filled */}
              <div className="rounded-md border border-soft/60 bg-bg p-4">
                <h3 className="font-ui text-ui-sm font-semibold text-ink">Summary</h3>
                <ul className="mt-2 space-y-1 font-ui text-ui-sm text-ink-light">
                  <li><strong>Slug:</strong> {form.slug || '(not set)'}</li>
                  <li><strong>Category:</strong> {form.category}</li>
                  <li><strong>Access:</strong> {article?.access_level ?? 'free'}</li>
                  <li>
                    <strong>Translations:</strong>{' '}
                    {LANGUAGES.filter((l) => form.translations[l.code].headline.trim()).map((l) => l.label).join(', ') || 'None'}
                  </li>
                  <li><strong>Cover image:</strong> {form.cover_image_url ? '✓' : '—'}</li>
                  <li><strong>Video:</strong> {form.video_url ? '✓' : '—'}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Step navigation */}
        <div className="mt-6 flex justify-between border-t border-soft/40 pt-4">
          <button
            onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
            disabled={currentStep === 0}
            className="rounded-pill border border-soft bg-white px-4 py-2 font-ui text-ui-sm text-ink-light hover:bg-soft/20 disabled:opacity-40"
          >
            ← Previous
          </button>
          {currentStep < STEPS.length - 1 && (
            <button
              onClick={() => setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1))}
              className="rounded-pill bg-secondary px-4 py-2 font-ui text-ui-sm font-medium text-white hover:bg-secondary-700"
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </Container>
  );
}
